import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { LuArrowLeft, LuCircleCheck, LuTrash2 } from "react-icons/lu";
import { Loader, Spinner } from "../../components/ui/Loader";
import {
  fetchProject,
  updateProject,
  deleteProject,
  fetchProjectStageValues,
  createProjectStageValue,
  updateProjectStageValue,
  AdminProjectUpdate,
} from "../../lib/admin";
import { sendProjectNotification } from "../../lib/telegram";
import { insertProjectNotification } from "../../lib/notifications";
import { formatDate, formatMoney } from "../../lib/projects";
import { isValidPhone } from "../../lib/profiles";
import {
  PROJECT_STATUSES,
  PROJECT_STATUS_LABELS,
  Project,
  ProjectStatus,
  ProjectStageValue,
} from "../../models/project";

interface StageDraft {
  id: number | null;
  status: ProjectStatus;
  contract_amount: string;
  bonus_amount: string;
  manager_comment: string;
}

const toStageDraft = (v: ProjectStageValue, fallback: ProjectStatus = "new"): StageDraft => ({
  id: v.id,
  status: v.status ?? fallback,
  contract_amount: v.contract_amount != null ? String(v.contract_amount) : "",
  bonus_amount: v.bonus_amount != null ? String(v.bonus_amount) : "",
  manager_comment: v.manager_comment ?? "",
});

const emptyStageDraft = (status: ProjectStatus = "new"): StageDraft => ({
  id: null,
  status,
  contract_amount: "",
  bonus_amount: "",
  manager_comment: "",
});

export const AdminProjectCard = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [notifySent, setNotifySent] = useState(false);
  const [notifyError, setNotifyError] = useState<string | null>(null);
  const [notifyModal, setNotifyModal] = useState(false);
  const prevBonusRef = useRef<number | null | undefined>(undefined);
  const prevStatusRef = useRef<ProjectStatus | undefined>(undefined);
  const prevCommentRef = useRef<string | null | undefined>(undefined);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [name, setName] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [address, setAddress] = useState("");
  const [areaM2, setAreaM2] = useState("");
  const [stages, setStages] = useState<StageDraft[]>([]);

  useEffect(() => {
    if (!id) return;
    Promise.all([fetchProject(id), fetchProjectStageValues(id)])
      .then(([p, values]) => {
        if (!p) setError("Проєкт не знайдено");
        setProject(p);
        setStages(
          values.length
            ? values.map((v) => toStageDraft(v, p?.status))
            : [emptyStageDraft(p?.status)]
        );
        if (p) {
          setName(p.name);
          setClientName(p.client_name ?? "");
          setClientPhone(p.client_phone ?? "");
          setAddress(p.address ?? "");
          setAreaM2(p.area_m2 != null ? String(p.area_m2) : "");
        }
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Помилка завантаження"))
      .finally(() => setLoading(false));
  }, [id]);

  const updateStageField = (
    idx: number,
    field: "status" | "contract_amount" | "bonus_amount" | "manager_comment",
    value: string
  ) => {
    setStages((prev) =>
      prev.map((s, i) => (i === idx ? ({ ...s, [field]: value } as StageDraft) : s))
    );
  };

  const handleAddStage = () => {
    setStages((prev) => [...prev, emptyStageDraft(prev[prev.length - 1]?.status)]);
  };

  const parseMoney = (v: string): number | null => {
    const n = Number(v.replace(",", "."));
    return v.trim() === "" || Number.isNaN(n) ? null : n;
  };

  const handleSave = async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!id || !project) return;
    setError(null);
    setSaved(false);
    if (!name.trim()) {
      setError("Вкажіть назву проєкту");
      return;
    }
    if (clientPhone.trim() && !isValidPhone(clientPhone)) {
      setError("Вкажіть коректний номер телефону клієнта");
      return;
    }
    const area = parseMoney(areaM2);
    if (areaM2.trim() !== "" && area === null) {
      setError("Некоректна площа");
      return;
    }
    const parsedStages: { draft: StageDraft; contract: number | null; bonus: number | null }[] = [];
    for (const draft of stages) {
      const stageContract = parseMoney(draft.contract_amount);
      if (draft.contract_amount.trim() !== "" && stageContract === null) {
        setError("Некоректна сума договору");
        return;
      }
      const stageBonus = parseMoney(draft.bonus_amount);
      if (draft.bonus_amount.trim() !== "" && stageBonus === null) {
        setError("Некоректна сума бонусу");
        return;
      }
      parsedStages.push({ draft, contract: stageContract, bonus: stageBonus });
    }
    setSaving(true);
    try {
      const savedStages: ProjectStageValue[] = [];
      for (const { draft, contract: stageContract, bonus: stageBonus } of parsedStages) {
        const stageComment = draft.manager_comment.trim() || null;
        const values = {
          status: draft.status,
          contract_amount: stageContract,
          bonus_amount: stageBonus,
          manager_comment: stageComment,
        };
        const row =
          draft.id == null
            ? await createProjectStageValue(id, values)
            : await updateProjectStageValue(draft.id, values);
        savedStages.push(row);
      }
      setStages(savedStages.map((v) => toStageDraft(v)));
      const status = savedStages[savedStages.length - 1]?.status ?? project.status;
      const totalContract = savedStages.reduce(
        (sum, v) => sum + (v.contract_amount ?? 0),
        0
      );
      const totalBonus = savedStages.reduce(
        (sum, v) => sum + (v.bonus_amount ?? 0),
        0
      );
      const lastComment =
        [...savedStages].reverse().find((v) => v.manager_comment)?.manager_comment ?? null;
      const update: AdminProjectUpdate = {
        name: name.trim(),
        client_name: clientName.trim() || null,
        client_phone: clientPhone.trim() || null,
        address: address.trim() || null,
        area_m2: area,
        status,
        contract_amount: totalContract,
        bonus_amount: totalBonus,
        manager_comment: lastComment,
      };
      const prevBonus = project.bonus_amount;
      const updated = await updateProject(id, update);
      setProject(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      const statusChanged = updated.status !== project.status;
      const bonusChanged = updated.bonus_amount !== prevBonus;
      const commentChanged = updated.manager_comment !== project.manager_comment;
      if (statusChanged || bonusChanged || commentChanged) {
        prevBonusRef.current = bonusChanged ? prevBonus : undefined;
        prevStatusRef.current = statusChanged ? project.status : undefined;
        prevCommentRef.current = commentChanged ? project.manager_comment : undefined;
        setNotifyModal(true);
        setNotifySent(false);
      }
    } catch (err) {
      setError("Помилка збереження");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteProject(id);
      navigate("/admin/projects");
    } catch (err) {
      setError("Помилка видалення");
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleNotify = async () => {
    if (!project) return;
    setSaving(true);
    setNotifyError(null);
    try {
      const bonusChanged = prevBonusRef.current !== undefined;
      const statusChanged = prevStatusRef.current !== undefined;
      const kind = statusChanged ? "status" : bonusChanged ? "bonus" : "comment";
      await insertProjectNotification(
        {
          id: project.id,
          name: project.name,
          status: project.status,
          bonus_amount: project.bonus_amount,
          owner_id: project.owner_id,
          manager_comment: project.manager_comment,
        },
        kind
      );
      await sendProjectNotification(
        statusChanged
          ? {
              event: "status_change",
              project_id: project.id,
              project_name: project.name,
              project_code: project.project_code,
              owner_id: project.owner_id,
              status: PROJECT_STATUS_LABELS[project.status],
              bonus_amount: bonusChanged ? project.bonus_amount : undefined,
              manager_comment: project.manager_comment,
            }
          : bonusChanged
            ? {
                event: "bonus_change",
                project_id: project.id,
                project_name: project.name,
                project_code: project.project_code,
                owner_id: project.owner_id,
                bonus_amount: project.bonus_amount,
              }
            : {
                event: "comment_change",
                project_id: project.id,
                project_name: project.name,
                project_code: project.project_code,
                owner_id: project.owner_id,
                manager_comment: project.manager_comment,
              }
      );
      prevBonusRef.current = undefined;
      prevStatusRef.current = undefined;
      prevCommentRef.current = undefined;
      setNotifySent(true);
      setTimeout(() => {
        setNotifyModal(false);
        setNotifySent(false);
      }, 3000);
    } catch (err) {
      setNotifyError("Сталася помилка. Спробуйте ще раз");
    } finally {
      setSaving(false);
    }
  };

  const totalContract = stages.reduce(
    (sum, s) => sum + (parseMoney(s.contract_amount) ?? 0),
    0
  );
  const totalBonus = stages.reduce(
    (sum, s) => sum + (parseMoney(s.bonus_amount) ?? 0),
    0
  );

  return (
    <main className="mx-auto w-full max-w-cabinet flex-1 pb-7">
      <div className="px-5 md:px-8 pt-5 pb-1 sticky top-0 z-10 backdrop-blur-md">
        <div className="flex items-center justify-between">
          <Link to="/admin/projects" className="link-brand gap-1">
            <LuArrowLeft aria-hidden />
            Проєкти
          </Link>
          {project && !loading && (
            <div className="flex items-center gap-5">
              {error && (
                <p className="hidden sm:block rounded bg-red-500/10 px-4 py-3 text-sm text-red-400">
                  {error}
                </p>
              )}
              {!saved ? (
                <>
                  <button
                    type="submit"
                    form="project-edit-form"
                    disabled={saving}
                    className="btn-brand px-4 py-3 justify-center disabled:opacity-70"
                  >
                    {saving ? (
                      <>
                        <Spinner className="h-4 w-4" />
                        Зачекайте…
                      </>
                    ) : (
                      "Зберегти"
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(true)}
                    disabled={deleting}
                    aria-label="Видалити проєкт"
                    title="Видалити проєкт"
                    className="p-3 rounded-lg border border-neutral-700 text-neutral-400 transition-colors hover:border-red-500/60 hover:text-red-400 disabled:opacity-70"
                  >
                    <LuTrash2 aria-hidden className="h-5 w-5" />
                  </button>
                </>
              ) : (
                <p className="rounded bg-green-500/10 px-4 py-3 text-sm text-green-400">
                  Збережено
                </p>
              )}
            </div>
          )}
        </div>
        {error && (
          <p className="sm:hidden mt-1 w-fit rounded-lg bg-red-500/10 px-2.5 py-1 text-sm text-red-400">
            {error}
          </p>
        )}
      </div>

      {notifyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-5">
          <div className="absolute inset-0 bg-black/70" onClick={() => setNotifyModal(false)}></div>
          <div className="z-10 w-full max-w-sm rounded-xl border border-neutral-700 bg-neutral-900 p-5">
            {notifySent ? (
              <div className="flex items-center justify-center gap-3">
                <LuCircleCheck className="text-3xl text-green-400" aria-hidden />
                <h2 className="text-2xl font-bold text-white">Надіслано</h2>
              </div>
            ) : (
              <>
                <p className="text-neutral-200">
                  Статус проєкту змінено.<br/>Надіслати партнеру сповіщення?
                </p>
                {notifyError && (
                  <p className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
                    {notifyError}
                  </p>
                )}
                <div className="mt-5 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setNotifyModal(false)}
                    disabled={saving}
                    className="rounded border border-neutral-600 px-5 py-2.5 text-sm text-neutral-300 transition-colors hover:border-neutral-400 disabled:opacity-70"
                  >
                    Ні
                  </button>
                  <button
                    type="button"
                    onClick={handleNotify}
                    disabled={saving}
                    className="btn-brand px-5 py-2.5 text-sm justify-center disabled:opacity-70"
                  >
                    {saving ? "Надсилання…" : "Надіслати"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-5">
          <div className="absolute inset-0 bg-black/70" onClick={() => setConfirmDelete(false)}></div>
          <div className="z-10 w-full max-w-sm rounded-xl border border-neutral-700 bg-neutral-900 p-5">
            <p className="text-neutral-200">
              Видалити проєкт «{project?.name}»?
            </p>
            <div className="mt-5 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="rounded bg-red-500 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-red-600 disabled:opacity-70"
              >
                {deleting ? "Видалення…" : "Видалити"}
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
                className="rounded-lg border border-neutral-600 px-5 py-2.5 text-sm text-neutral-300 transition-colors hover:border-neutral-400 disabled:opacity-70"
              >
                Ні
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="px-5 md:px-8">
      {loading ? (
        <Loader />
      ) : error && !project ? (
        <p className="mt-3 rounded border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
          {error}
        </p>
      ) : project ? (          
        <form id="project-edit-form" onSubmit={handleSave} className="contents">
          <div className="mt-3 flex flex-col lg:flex-row gap-7">
            <div className="flex-1 flex flex-col gap-6">
              <fieldset className="card p-5">
                <legend className="px-2 text-sm font-bold uppercase tracking-wider text-neutral-400">
                  Договір
                </legend>
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col">
                    <div className="flex flex-col gap-3">
                    {stages.map((draft, idx) => (
                      <div key={draft.id ?? `new-${idx}`} className="flex flex-col gap-2">
                        <label className="block">
                          <span className="mb-2 block text-sm font-semibold text-neutral-300">
                            Статус
                          </span>
                          <select
                            className="input-dark"
                            value={draft.status}
                            onChange={(e) => updateStageField(idx, "status", e.target.value)}
                          >
                            {PROJECT_STATUSES.map((s) => (
                              <option key={s} value={s} className="text-black">
                                {PROJECT_STATUS_LABELS[s]}
                              </option>
                            ))}
                          </select>
                        </label>
                        <div className="flex gap-3">
                          <label className="block w-2/3">
                            <span className="mb-2 block text-sm font-semibold text-neutral-300">
                              Сума договору, €
                            </span>
                            <input
                              className="input-dark"
                              type="number"
                              min="0"
                              step="0.01"
                              value={draft.contract_amount}
                              onChange={(e) =>
                                updateStageField(idx, "contract_amount", e.target.value)
                              }
                            />
                          </label>
                          <label className="block w-1/3">
                            <span className="mb-2 block text-sm font-semibold text-neutral-300">
                              Бонус, €
                            </span>
                            <input
                              className="input-dark"
                              type="number"
                              min="0"
                              step="0.01"
                              value={draft.bonus_amount}
                              onChange={(e) =>
                                updateStageField(idx, "bonus_amount", e.target.value)
                              }
                            />
                          </label>
                        </div>
                        <label className="block">
                          <span className="mb-2 block text-sm font-semibold text-neutral-300">
                            Коментар менеджера
                          </span>
                          <textarea
                            className="input-dark"
                            value={draft.manager_comment}
                            onChange={(e) =>
                              updateStageField(idx, "manager_comment", e.target.value)
                            }
                          />
                        </label>
                      </div>
                    ))}
                    </div>
                    <button
                      type="button"
                      onClick={handleAddStage}
                      className="self-start mt-2 rounded-lg border border-neutral-700 px-4 py-2.5 text-sm text-neutral-300 transition-colors hover:border-neutral-500"
                    >
                      Додати етап
                    </button>
                    <div className="rounded-lg border border-neutral-800 p-3 mt-7">
                      <div className="flex gap-3">
                        <div className="block w-2/3">
                          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-neutral-200">
                            Сума проєкту, €
                          </span>
                          <p className="text-neutral-300">{formatMoney(totalContract)}</p>
                        </div>
                        <div className="block w-1/3">
                          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-neutral-200">
                            Загальний бонус, €
                          </span>
                          <p className="text-neutral-300">{formatMoney(totalBonus)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </fieldset>
            </div>
            
            <div className="flex-1 flex flex-col gap-6">        
              <fieldset className="card p-5">
                <legend className="px-2 text-sm font-bold uppercase tracking-wider text-neutral-400">
                  Дані клієнта
                </legend>
                <div className="flex flex-col gap-3">
                  <label className="block">
                    <span className="mb-2 block text-sm font-semibold text-neutral-300">
                      Клієнт
                    </span>
                    <input
                      className="input-dark"
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                    />
                  </label>
                  <label className="block">
                    <span className="mb-2 block text-sm font-semibold text-neutral-300">
                      Телефон клієнта
                    </span>
                    <input
                      className="input-dark"
                      type="tel"
                      value={clientPhone}
                      onChange={(e) => setClientPhone(e.target.value)}
                    />
                  </label>
                </div>
              </fieldset>

              <fieldset className="card p-5">
                <legend className="px-2 text-sm font-bold uppercase tracking-wider text-neutral-400">
                  Дані обʼєкта
                </legend>
                <div className="flex flex-col gap-3">
                  <label className="block">
                    <span className="mb-2 block text-sm font-semibold text-neutral-300">
                      Назва
                    </span>
                    <input
                      className="input-dark"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                  </label>
                  <label className="block">
                    <span className="mb-2 block text-sm font-semibold text-neutral-300">
                      Адреса
                    </span>
                    <input
                      className="input-dark"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                    />
                  </label>
                  <label className="block">
                    <span className="mb-2 block text-sm font-semibold text-neutral-300">
                      Площа, м²
                    </span>
                    <input
                      className="input-dark"
                      type="number"
                      min="0"
                      step="0.1"
                      value={areaM2}
                      onChange={(e) => setAreaM2(e.target.value)}
                    />
                  </label>
                </div>
              </fieldset>

              <fieldset className="card p-5">
                <legend className="px-2 text-sm font-bold uppercase tracking-wider text-neutral-400">
                  Деталі
                </legend>
                <div className="flex flex-col gap-3">
                  <label className="block">
                    <span className="mb-2 block text-sm font-semibold text-neutral-300">
                      Код
                    </span>
                    <input
                      className="input-dark"
                      value={project.project_code ?? "—"}
                      readOnly
                    />
                  </label>
                  <label className="block">
                    <span className="mb-2 block text-sm font-semibold text-neutral-300">
                      Дата подачі
                    </span>
                    <input
                      className="input-dark"
                      value={formatDate(project.submitted_at)}
                      readOnly
                    />
                  </label>
                </div>
              </fieldset>
            </div>
          </div>
        </form>
      ) : null}
      </div>
    </main>
  );
};
